import { type CSSProperties } from "react";

import clsx from "clsx";

import { useShowTableSelectColumn } from "../../../hooks/use-show-table-select-column";
import {
  ACTUAL_FORECAST_PERIOD_ID,
  ACTUAL_WEEKLY_SPEND_ID,
  AVG_WEEKLY_SPEND_ID,
  BUDGETED_WEEKLY_SPEND_ID,
  BUDGET_FORECAST_PERIOD_ID,
  FIXED_NAME_COLUMN_ID,
  FIXED_STATUS_COLUMN_ID,
  PACING_STATUS_COLUMN_ID,
  SELECTION_COLUMN_WIDTH,
  SPEND_THERMOMETER_ID,
  TOTAL_BUDGET_COLUMN_ID,
} from "../constants";
import {
  MetricDataFormatter,
  PacingStatusDataFormatter,
  TotalBudgetDataFormatter,
} from "../data-type-formatters";
import { SpendThermometerDataFormatter } from "../data-type-formatters/spend-thermometer-data-formatter";
import { type PerformanceTrackingTableRow } from "../performance-tracking-table";

import { AvgWeeklySpendFixedRowDataFormatter } from "./avg-weekly-spend-fixed-row-data-formatter";
import { ForecastPeriodFixedRowDataFormatter } from "./forecast-period-fixed-row-data-formatter";
import { WeeklyPeriodFixedRowDataFormatter } from "./weekly-period-fixed-row-data-formatter";

// This is a simplification of what the actual type injected into the component by @devexpress is.
type Cell = {
  colSpan: number;
  column: {
    type?: unknown;
    column: {
      id: string;
      compareDataLoading: boolean;
      name: string;
      title: string;
    };
  };
};

interface CustomTableHeadWithFixedRowProps {
  readonly isFixed: boolean;
  readonly children: JSX.Element[];
  className?: string;
  style?: CSSProperties;
}

export const FixedRow =
  (
    tableSummary: PerformanceTrackingTableRow,
    expandedRowIds: (string | number)[],
    onCollapseAllRows: () => void,
  ) =>
  ({
    isFixed,
    className,
    style,
    children,
  }: CustomTableHeadWithFixedRowProps) => {
    const columnsInView: Cell[] = children[0].props.cells;

    return (
      <thead
        className={clsx(
          {
            "dx-g-bs4-fixed-header": isFixed,
            "dx-g-bs4-table-sticky": isFixed,
            "bg-v2-primary": isFixed,
          },
          className,
        )}
        style={style}
      >
        {children}
        <tr style={{ boxShadow: "0px 4px 8px 0px rgba(2, 18, 48, 0.12)" }}>
          {columnsInView.map(({ column }: Cell, index: number) => (
            <ColumnHead
              key={index}
              column={column}
              tableSummary={tableSummary}
              expandedRowIds={expandedRowIds}
              onCollapseAllRows={onCollapseAllRows}
            />
          ))}
        </tr>
      </thead>
    );
  };

const ColumnHead = ({
  column,
  tableSummary,
  expandedRowIds,
  onCollapseAllRows,
}: {
  column: Cell["column"];
  tableSummary: PerformanceTrackingTableRow;
  expandedRowIds: (string | number)[];
  onCollapseAllRows: () => void;
}) => {
  const [showSelectColumn] = useShowTableSelectColumn();
  // Not sure why but there's s stub header column
  const columnType = column?.type?.toString();
  if (columnType && ["Symbol(stub)", "Symbol(flex)"].includes(columnType)) {
    return <th className="p-0 table-summary-cell z-2" scope="col"></th>;
  }

  if (columnType === "Symbol(select)") {
    return (
      <th
        className="table-summary-cell left-0 p-0 dx-g-bs4-fixed-cell position-sticky z-2"
        scope="col"
      ></th>
    );
  }

  // Only a known data column would have a column property
  if (!column?.column) {
    return null;
  }

  if (column.column.name === FIXED_NAME_COLUMN_ID) {
    const leftOffset = showSelectColumn ? SELECTION_COLUMN_WIDTH : 0;
    return (
      <th
        className="table-summary-cell position-sticky z-2"
        scope="col"
        style={{ left: `${leftOffset}px` }}
      >
        <div className="flex items-center h-full px-3">
          <div className="flex flex-col justify-center leading-3">
            <div>Total / AVG</div>
            {expandedRowIds.length > 0 && (
              <button
                className={clsx("font-normal text-[0.65rem] text-cosmic", {
                  "mt-2": true,
                })}
                onClick={() => onCollapseAllRows()}
              >
                <i className="fa-regular fa-sharp fa-arrows-to-line mr-1"></i>
                Collapse all rows
              </button>
            )}
          </div>
        </div>
      </th>
    );
  }

  if (column.column.name === FIXED_STATUS_COLUMN_ID) {
    return <th className="table-summary-cell z-2" scope="col"></th>;
  }
  if (column.column.name === SPEND_THERMOMETER_ID) {
    return (
      <th key={column.column.id} className="table-summary-cell z-2" scope="col">
        <SpendThermometerDataFormatter
          row={tableSummary}
          column={column.column}
          value={null}
        />
      </th>
    );
  }

  if (
    [BUDGET_FORECAST_PERIOD_ID, ACTUAL_FORECAST_PERIOD_ID].includes(
      column.column.name,
    )
  ) {
    return (
      <th key={column.column.id} className="table-summary-cell z-2" scope="col">
        <ForecastPeriodFixedRowDataFormatter
          column={column.column}
          row={tableSummary}
          value={null}
        />
      </th>
    );
  }

  if (
    [BUDGETED_WEEKLY_SPEND_ID, ACTUAL_WEEKLY_SPEND_ID].includes(
      column.column.name,
    )
  ) {
    return (
      <th key={column.column.id} className="table-summary-cell z-2" scope="col">
        <WeeklyPeriodFixedRowDataFormatter
          column={column.column}
          row={tableSummary}
          value={null}
        />
      </th>
    );
  }

  if (column.column.name === AVG_WEEKLY_SPEND_ID) {
    return (
      <th key={column.column.id} className="table-summary-cell z-2" scope="col">
        <AvgWeeklySpendFixedRowDataFormatter
          row={tableSummary}
          column={column.column}
          value={null}
        />
      </th>
    );
  }

  if (column.column.name === PACING_STATUS_COLUMN_ID) {
    return (
      <th key={column.column.id} className="table-summary-cell z-2" scope="col">
        <PacingStatusDataFormatter
          row={tableSummary}
          column={column.column}
          value={null}
        />
      </th>
    );
  }

  if (column.column.name === TOTAL_BUDGET_COLUMN_ID) {
    return (
      <th key={column.column.id} className="table-summary-cell z-2" scope="col">
        <TotalBudgetDataFormatter
          row={tableSummary}
          column={column.column}
          value={null}
        />
      </th>
    );
  }

  return (
    <th key={column.column.id} className="table-summary-cell z-2" scope="col">
      <MetricDataFormatter
        row={tableSummary}
        column={column.column}
        value={null}
      />
    </th>
  );
};
